
import { graphql, gqlRequest } from '@client/shared/graphql';
import { type ResultOf } from 'gql.tada';
import type { Role } from '@shared/user';

type GroupUserRow = ResultOf<typeof GroupUsersQuery>['users'][number];

export type GroupUser = Omit<GroupUserRow, 'roles'> & {
  readonly roles: readonly Role[];
};

function toGroupUser(user: GroupUserRow): GroupUser {
  return { ...user, roles: user.roles as Role[] };
}

export const GroupUsersQuery = graphql(`
  query GroupUsers {
    users {
      id
      name
      email
      roles
      isActive
      pending
      createdAt
    }
  }
`);

export const GROUP_USERS_QUERY_KEY = ['users', 'group'] as const;

export async function fetchGroupUsers(signal?: AbortSignal): Promise<GroupUser[]> {
  const data = await gqlRequest(GroupUsersQuery, {}, { signal });
  return data.users.map(toGroupUser);
}

export const InviteUserMutation = graphql(`
  mutation InviteUser($email: String!, $name: String!, $roles: [String!]!) {
    inviteUser(email: $email, name: $name, roles: $roles) {
      id
      name
      email
      roles
      isActive
      pending
      createdAt
    }
  }
`);

export async function inviteUser(variables: {
  email: string;
  name: string;
  roles: Role[];
}): Promise<GroupUser> {
  const data = await gqlRequest(InviteUserMutation, variables);
  return toGroupUser(data.inviteUser);
}

export const UpdateUserRolesMutation = graphql(`
  mutation UpdateUserRoles($userId: ID!, $roles: [String!]!) {
    updateUserRoles(userId: $userId, roles: $roles) {
      id
      name
      email
      roles
      isActive
      pending
      createdAt
    }
  }
`);

export async function updateUserRoles(variables: {
  userId: string;
  roles: Role[];
}): Promise<GroupUser> {
  const data = await gqlRequest(UpdateUserRolesMutation, variables);
  return toGroupUser(data.updateUserRoles);
}

export const SetUserActiveMutation = graphql(`
  mutation SetUserActive($userId: ID!, $active: Boolean!) {
    setUserActive(userId: $userId, active: $active) {
      id
      name
      email
      roles
      isActive
      pending
      createdAt
    }
  }
`);

export async function setUserActive(variables: {
  userId: string;
  active: boolean;
}): Promise<GroupUser> {
  const data = await gqlRequest(SetUserActiveMutation, variables);
  return toGroupUser(data.setUserActive);
}
